import { toZonedTime } from "date-fns-tz";
import type { CalendarEvent, CalendarPet } from "@/components/calendar/views/CalendarEventTypes";
import type { AppointmentWithDetails, Veterinarian } from "@/hooks/useAppointments";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";
import { buildAppointmentSummaryItems } from "@/lib/utils/appointment-summary-items";
import { getEffectiveAppointmentStatusName } from "@/lib/utils/clinic-appointment-status";

export const CALENDAR_TIMEZONE = "America/Sao_Paulo";

type BuildContext = {
  professionalsById: Map<string, Veterinarian>;
  t: (key: string) => string;
};

export function buildCalendarEvents(
  appointments: AppointmentWithDetails[],
  { professionalsById, t }: BuildContext,
): CalendarEvent[] {
  return appointments.map((appointment) => {
    const startDate = toZonedTime(new Date(appointment.starts_at), CALENDAR_TIMEZONE);
    const endDate = toZonedTime(new Date(appointment.ends_at), CALENDAR_TIMEZONE);

    const pets = (appointment.pets ?? []) as unknown as CalendarPet[];
    const primaryPet = (appointment.pet ?? null) as unknown as CalendarPet | null;
    const names = pets.map((p) => p?.name?.trim?.() ?? "").filter(Boolean);
    const title = names.length > 0 ? names.join(", ") : (primaryPet?.name ?? t("appointmentLabel"));

    const vetUserId = appointment.vet_user_id ?? null;
    const resourceId = vetUserId ?? "unassigned";
    const resolvedProfessional = vetUserId ? professionalsById.get(vetUserId) : undefined;
    const resolvedVetName =
      (appointment.vet as { full_name?: string | null } | null | undefined)?.full_name ??
      resolvedProfessional?.full_name ??
      null;
    const summaryItems = buildAppointmentSummaryItems(appointment, t);

    return {
      id: appointment.id,
      title,
      start: startDate,
      end: endDate,
      type: appointment.type,
      clinic_appointment_type: appointment.clinic_appointment_type ?? null,
      vet_user_id: vetUserId,
      resourceId,
      pet: primaryPet,
      pets,
      owner: (appointment.owner ?? null) as unknown as CalendarEvent["owner"],
      vet: resolvedVetName
        ? ({
            full_name: resolvedVetName,
            avatar_url: (appointment.vet as { avatar_url?: string | null } | null | undefined)?.avatar_url ?? null,
            role: resolvedProfessional?.role ?? null,
          } as CalendarEvent["vet"])
        : null,
      notes: appointment.notes,
      status: getEffectiveAppointmentStatusName(appointment),
      is_taxi_dog: appointment.is_taxi_dog ?? false,
      taxi_dog_type: appointment.taxi_dog_type ?? null,
      financial_status: deriveFinancialStatusWithLegacyFallback(
        appointment.appointment_financial,
        { status: appointment.status, grooming_status: appointment.grooming_status ?? null },
        appointment.linked_sale,
      ),
      summary_items: summaryItems,
    };
  });
}
